<template>
  <Report
    :report-id="reportId"
    title="New Contacts"
    tooltip-text="A trend showing the number of recently created contacts, split by whether they are currently a lead or a user"
    class="chart-container"
    :dataEmpty="isDataEmpty"
    :loadedData="isDataLoaded"
    :loadingError="loadingError"
  >
    <template #header-addons>
      <span class="button" :class="{ 'is-static': showLineChart }" @click="switchLineChart()">
        <font-awesome-icon icon="chart-line"></font-awesome-icon>
      </span>
      <span
        class="button percent-button"
        :class="{ 'is-static': showPercentageBarChart }"
        @click="switchStackedChart()"
      >
        <font-awesome-icon icon="percent"></font-awesome-icon>
      </span>
      <span class="button" :class="{ 'is-static': showBarChart }" @click="switchBarChart()">
        <font-awesome-icon icon="chart-bar"></font-awesome-icon>
      </span>
    </template>

    <template #chart>
      <LineChart
        :reportId="reportId"
        class="chart"
        v-if="showLineChart"
        :data="reportData"
        :fill="false"
        :allowDecimalTicksY="false"
        :seriesOrder="seriesOrder"
      />
      <PercentageStackedBarChart
        :reportId="reportId"
        class="chart"
        v-if="showPercentageBarChart"
        :data="reportData"
        :seriesOrder="seriesOrder"
      />
      <BarChart :reportId="reportId" class="chart" v-if="showBarChart" :data="reportData" :seriesOrder="seriesOrder" />
    </template>
  </Report>
</template>

<script lang="ts" setup>
import Report from '../Report.vue';
import PercentageStackedBarChart from '../chartjs/PercentageStackedBarChart.vue';
import BarChart from '../chartjs/BarChart.vue';
import LineChart from '../chartjs/LineChart.vue';
import { Data, Series } from '../../models';
import { ChartType } from '../../models/chartType';
import { computed, PropType, ref } from 'vue';
import { useReportData } from '@/features/reports/util/useReportData';

const props = defineProps({
  data: {
    type: [Object, undefined] as PropType<Data | undefined>,
    required: false,
  },
  loadedData: {
    type: Boolean,
    default: false,
  },
  loadingError: {
    type: Boolean,
    default: false,
  },
});

const { reportId, isDataLoaded, isDataEmpty, createBlankSeries } = useReportData(props);
const seriesOrder = ['Leads', 'Users'];

const chartType = ref(ChartType.BAR);
const reportData = computed(() => {
  return {
    scale: props.data.scale,
    series: seriesOrder.map(
      (seriesName) => props.data?.series.find((s: Series) => s.name === seriesName) || createBlankSeries(seriesName),
    ),
  };
});

const showLineChart = computed(() => chartType.value === ChartType.LINE);
const showBarChart = computed(() => chartType.value === ChartType.BAR);
const showPercentageBarChart = computed(() => chartType.value === ChartType.PERCENTAGE_BAR);

const switchBarChart = () => (chartType.value = ChartType.BAR);
const switchLineChart = () => (chartType.value = ChartType.LINE);
const switchStackedChart = () => (chartType.value = ChartType.PERCENTAGE_BAR);
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;
</style>
